import { useCallback, useEffect, useState } from "react";
import { Form, InputGroup, Table } from "react-bootstrap";
import CustomPagination from "../../components/CustomPagination";
import * as CustomToast from "../../components/CustomToastMessage";
import MyDateRangePicker from "../../components/MyDateRangePicker";
import NoDateFound from "../../components/NoDateFound";
import Loader from "../../components/loader/Loader";
import { useGlobalState } from "../../context/GlobalStateContext";
import Layout from "../../layout/Layout";
import axiosInstance from "../../utils/axiosConfig";
import AIChatView from "./AIChatView";

function AIChat() {
  const [aiChats, setAIChats] = useState([]);
  const {
    loading,
    setLoading,
    search,
    pagination,
    setPagination,
    handleSearch,
    handleLimitChange,
  } = useGlobalState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const fetchAIChats = useCallback(
    async (page = 1, limit = 10, searchQuery = "", from = "", to = "") => {
      
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/chatbot/history`,
          {
            params: {
              page,
              limit,
              search: (searchQuery || "").trim()
                ? searchQuery?.startsWith("+")
                  ? searchQuery?.substring(1)
                  : (searchQuery || "").trim()
                : undefined,
              startDate: from,
              endDate: to,
            },
          }
        );

        if (response.data.success) {
          setAIChats(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setAIChats([]);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          }));
        }
      } catch (error) {
        if (error.response.data.status === 400) {
          CustomToast.error(error.response?.data?.message);
        }
        console.error(error.response?.data?.message || "Internal server error");
        setAIChats([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        }));
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPagination]
  );

  useEffect(() => {
    fetchAIChats(
      pagination.currentPage,
      pagination.limit,
      search,
      startDate,
      endDate
    );
  }, [
    pagination.currentPage,
    pagination.limit,
    search,
    fetchAIChats,
    startDate,
    endDate,
  ]);

  function formatDate(dateString) {
    const date = new Date(dateString);

    // Example: "Dec 04, 2025 11:18 AM"
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  return (
    <Layout title="AI Chat List">
      <main>
        <div className="card page-card">
          <div className="card-header d-xl-flex justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <p>Show</p>
                <Form.Select
                  aria-label="Items per page"
                  value={pagination.limit}
                  onChange={handleLimitChange}
                  style={{ minWidth: "80px" }}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
                <p>entries</p>
              </div>
            </div>
            <div className="d-flex align-items-start align-items-md-center justify-content-end gap-2 flex-column flex-wrap flex-md-row mt-3 mt-xl-0">
              <MyDateRangePicker
                onDateChange={handleDateRangeChange}
                initialStartDate={startDate}
                initialEndDate={endDate}
              />
              <div className="header-search">
                <InputGroup>
                  <InputGroup.Text id="basic-addon1" className="bg-transparent">
                    <img src="/images/png/search.png" width={20} alt="" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    placeholder="search here..."
                    value={search}
                    onChange={handleSearch(fetchAIChats)}
                  />
                </InputGroup>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="overflow-auto">
              <Table responsive className="vertical-align">
                <thead>
                  <tr>
                    <th width="40">Id</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Prompt</th>
                    <th>Response</th>
                    <th>Created Date</th>
                    <th width="100" className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : aiChats?.length > 0 ? (
                    aiChats?.map((item, index) => (
                      <tr key={item._id}>
                        <td width="40">
                          <p className="mb-0">
                            {(pagination.currentPage - 1) * pagination.limit +
                              index +
                              1}
                          </p>
                        </td>

                        <td>
                          <p className="mb-0">{`${item?.user?.firstName} ${item?.user?.lastName}`}</p>
                        </td>

                        <td className="table-td-200">
                          <p className="mb-0">{item?.user?.email}</p>
                        </td>

                        <td>
                          <p className="mb-0 table-td-300 limit-lines">
                            {item?.prompt}
                          </p>
                        </td>

                        <td>
                          <p
                            className="mb-0 table-td-600 limit-lines"
                            dangerouslySetInnerHTML={{
                              __html: item.response ? item.response : "",
                            }}
                          />
                        </td>

                        <td className="table-td-150">
                          {item.createdAt
                            ? new Date(item.createdAt)
                                .toISOString()
                                .split("T")[0]
                            : "-"}
                        </td>
                        <td width="100" className="text-center">
                          <AIChatView aiChat={item} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <NoDateFound subMessage={""} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <CustomPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default AIChat;
